import React, { useEffect, useMemo, useRef, useState } from 'react';

const ITEM_HEIGHT = 44;
const PADDING_ITEMS = 1;
const VISIBLE_ROWS = 3;
const WHEEL_HEIGHT = ITEM_HEIGHT * VISIBLE_ROWS;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS = [0, 30];

function pad(n) {
  return String(n).padStart(2, '0');
}

function formatTime(hour, minute) {
  return `${pad(hour)}:${pad(minute)}`;
}

function parseTime(value) {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) {
    return { hour: 9, minute: 0, valid: false };
  }
  const [h, m] = value.split(':').map(Number);
  return { hour: h, minute: m, valid: true };
}

function getScrollYForIndex(index) {
  return index * ITEM_HEIGHT;
}

function getIndexFromScrollY(y, maxIndex) {
  const idx = Math.round(y / ITEM_HEIGHT);
  return Math.max(0, Math.min(maxIndex, idx));
}

function WheelColumn({ options, selected, onSelect, disabled, formatValue }) {
  const scrollRef = useRef(null);
  const interactingRef = useRef(false);
  const dragRef = useRef(null);
  const endTimerRef = useRef(null);
  const selectedRef = useRef(selected);
  const onSelectRef = useRef(onSelect);
  const optionsRef = useRef(options);
  const selectedIndex = Math.max(0, options.indexOf(selected));
  const [centerIndex, setCenterIndex] = useState(selectedIndex);

  selectedRef.current = selected;
  onSelectRef.current = onSelect;
  optionsRef.current = options;

  useEffect(() => {
    if (interactingRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = getScrollYForIndex(selectedIndex);
    setCenterIndex(selectedIndex);
  }, [selectedIndex, options.length]);

  const commitIndex = (index) => {
    const opts = optionsRef.current;
    const clamped = Math.max(0, Math.min(opts.length - 1, index));
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: getScrollYForIndex(clamped), behavior: 'smooth' });
    }
    setCenterIndex(clamped);
    const value = opts[clamped];
    if (value !== selectedRef.current) onSelectRef.current?.(value);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const finish = () => {
      interactingRef.current = false;
      dragRef.current = null;
      commitIndex(getIndexFromScrollY(el.scrollTop, optionsRef.current.length - 1));
    };

    const onWheel = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      interactingRef.current = true;
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
      el.scrollTop += e.deltaY;
      setCenterIndex(getIndexFromScrollY(el.scrollTop, optionsRef.current.length - 1));
      endTimerRef.current = setTimeout(finish, 120);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
    };
  }, [disabled]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCenterIndex(getIndexFromScrollY(el.scrollTop, optionsRef.current.length - 1));
  };

  const onPointerDown = (e) => {
    if (disabled) return;
    const el = scrollRef.current;
    if (!el) return;
    interactingRef.current = true;
    if (endTimerRef.current) clearTimeout(endTimerRef.current);
    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startScroll: el.scrollTop,
      moved: false,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    const el = scrollRef.current;
    if (!drag || !el || drag.pointerId !== e.pointerId) return;
    const delta = e.clientY - drag.startY;
    if (Math.abs(delta) > 2) drag.moved = true;
    el.scrollTop = drag.startScroll - delta;
  };

  const onPointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const el = scrollRef.current;
    const moved = drag.moved;
    interactingRef.current = false;
    dragRef.current = null;
    if (!el) return;

    if (!moved) {
      const rect = el.getBoundingClientRect();
      const offsetFromCenter = e.clientY - rect.top - WHEEL_HEIGHT / 2;
      const indexOffset = Math.round(offsetFromCenter / ITEM_HEIGHT);
      const current = getIndexFromScrollY(el.scrollTop, optionsRef.current.length - 1);
      commitIndex(current + indexOffset);
      return;
    }

    commitIndex(getIndexFromScrollY(el.scrollTop, optionsRef.current.length - 1));
  };

  return (
    <div className="wheel-time-column">
      <div className="wheel-time-frame">
        <div className="wheel-time-fade" aria-hidden="true" />
        <div
          ref={scrollRef}
          className="wheel-time-scroll"
          onScroll={onScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            interactingRef.current = false;
            dragRef.current = null;
          }}
        >
          {Array.from({ length: PADDING_ITEMS }).map((_, i) => (
            <div key={`pad-top-${i}`} className="wheel-time-item is-pad" />
          ))}
          {options.map((value, index) => {
            const distance = Math.abs(index - centerIndex);
            const distanceClass =
              distance === 0
                ? ' is-center'
                : distance === 1
                  ? ' is-near'
                  : ' is-far';
            return (
              <div
                key={`${value}-${index}`}
                role="option"
                aria-selected={index === centerIndex}
                className={`wheel-time-item${distanceClass}`}
              >
                {formatValue(value)}
              </div>
            );
          })}
          {Array.from({ length: PADDING_ITEMS }).map((_, i) => (
            <div key={`pad-bottom-${i}`} className="wheel-time-item is-pad" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WheelTimePicker({
  value,
  onChange,
  disabled = false,
  required = false,
  minTime = '',
  label = '',
  placeholder = 'בחר שעה',
}) {
  const parsed = parseTime(value);
  const displayHour = parsed.valid ? parsed.hour : 9;
  const displayMinute = parsed.valid
    ? MINUTE_OPTIONS.includes(parsed.minute)
      ? parsed.minute
      : MINUTE_OPTIONS[0]
    : 0;

  const hourOptions = useMemo(() => {
    if (!minTime) return HOUR_OPTIONS;
    const { hour: minH, minute: minM } = parseTime(minTime);
    return HOUR_OPTIONS.filter((hour) => {
      if (hour > minH) return true;
      if (hour < minH) return false;
      return MINUTE_OPTIONS.some((minute) => minute >= minM);
    });
  }, [minTime]);

  const minuteOptions = useMemo(() => {
    if (!minTime) return MINUTE_OPTIONS;
    const { hour: minH, minute: minM } = parseTime(minTime);
    if (displayHour !== minH) return MINUTE_OPTIONS;
    return MINUTE_OPTIONS.filter((minute) => minute >= minM);
  }, [minTime, displayHour]);

  const safeHour = hourOptions.includes(displayHour) ? displayHour : hourOptions[0] ?? 0;
  const safeMinute = minuteOptions.includes(displayMinute)
    ? displayMinute
    : minuteOptions[0] ?? 0;

  const didInitRef = useRef(false);
  useEffect(() => {
    if (disabled || didInitRef.current || value) return;
    if (!required) return;
    didInitRef.current = true;
    onChange?.(formatTime(safeHour, safeMinute));
  }, [disabled, value, required, safeHour, safeMinute, onChange]);

  const commit = (hour, minute) => {
    if (disabled) return;
    onChange?.(formatTime(hour, minute));
  };

  return (
    <div className={`wheel-time-picker${disabled ? ' is-disabled' : ''}`}>
      <input type="hidden" value={value || ''} required={required} readOnly />
      <div className="wheel-time-preview">{label || value || placeholder}</div>
      <div className="wheel-time-shell">
        <div className="wheel-time-labels">
          <span>שעות</span>
          <span className="wheel-time-colon-gap" />
          <span>דקות</span>
        </div>
        <div className="wheel-time-wheels" style={{ height: WHEEL_HEIGHT }}>
          <div className="wheel-time-selection-band" aria-hidden="true" />
          <div className="wheel-time-colon" aria-hidden="true">
            :
          </div>
          <div className="wheel-time-row">
            <WheelColumn
              options={hourOptions.length ? hourOptions : HOUR_OPTIONS}
              selected={safeHour}
              onSelect={(hour) => {
                const { hour: minH, minute: minM } = parseTime(minTime);
                const minutes =
                  minTime && hour === minH
                    ? MINUTE_OPTIONS.filter((m) => m >= minM)
                    : MINUTE_OPTIONS;
                const minute = minutes.includes(safeMinute) ? safeMinute : minutes[0] ?? 0;
                commit(hour, minute);
              }}
              disabled={disabled}
              formatValue={pad}
            />
            <span className="wheel-time-colon-gap" />
            <WheelColumn
              options={minuteOptions.length ? minuteOptions : MINUTE_OPTIONS}
              selected={safeMinute}
              onSelect={(minute) => commit(safeHour, minute)}
              disabled={disabled}
              formatValue={pad}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
